import { type ReactElement, useEffect } from 'react'
import { useSpacingToken } from '../../theme/hooks/use-spacing-token'
import { type DataTestIdAttribute } from '../../types'
import { type TypographyProps } from '../../primitives/Typography'
import { Box } from '../../primitives/Box'
import { Stack } from '../../primitives/Stack'
import { Ellipsis } from '../../primitives/Ellipsis'
import { useCard } from './use-card'

type InternalProps = {
  title: string
  summary?: string | null
  endNode?: ReactElement | null
  isOpen?: boolean

  /**
   * What element do you want to give our title
   *
   * @default 'p'
   */
  component?: TypographyProps['component']

  /**
   * When your design needs something else then our default
   * @default 'h5'
   */
  variant?: 'h4' | 'h5'
} & DataTestIdAttribute

export type CardHeaderProps = Omit<InternalProps, 'summary'>

// internal component
const CardHeaderInternal = ({
  summary,
  title,
  isOpen,
  endNode,
  component = 'p',
  variant = 'h5',
  ...rest
}: InternalProps) => {
  const spacingToken = useSpacingToken()
  const { isDense, isMobile, setHasHeader } = useCard()

  useEffect(() => {
    setHasHeader(true)

    return () => setHasHeader(false)
  }, [setHasHeader])

  return (
    <Box sx={{ px: isDense ? 2 : spacingToken, pt: isMobile ? 2 : 2.5, pb: 0, width: '100%' }} {...rest}>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1, minHeight: ({ spacing }) => spacing(3.75) }}>
        <Box sx={{ flex: 1, textAlign: 'left' }}>
          <Ellipsis variant={variant} component={component}>
            {title}
          </Ellipsis>
          {!isOpen && summary && <Ellipsis variant={isMobile ? 'body2' : 'body1'}>{summary}</Ellipsis>}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'nowrap',
            alignItems: 'center',
            ml: 'auto',
            alignSelf: 'center',
            gap: 1,
          }}
        >
          {endNode}
        </Box>
      </Stack>
    </Box>
  )
}

export const BaseCardHeader = (props: CardHeaderProps) => <CardHeaderInternal {...props} />

export const BaseCardHeaderAccordion = (props: InternalProps) => <CardHeaderInternal {...props} />
